import React, { useContext, useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import moment from "moment";
import { useTranslation } from "react-i18next";
import BlockPopUp from "./BlockPopUp";
import { getBlockInfoFloodURL } from "../../../Services/EndPoints";
import { getData } from "../../../Services/Services";
import { ThemeContext } from "../../..";
import BlockDetails from "./BlockDetails";
import ToggleView from "./ToggleView/ToggleView";
import NoData from "../../../Components/UiComponents/NoData/NoData";

const buttonStyle = {
  padding: "4px 12px",
  borderRadius: "20px",
  backgroundColor: "#EEF6FB",
  color: "#2A6591",
  border: "1px solid rgba(42, 101, 145, 0.16)",
  fontSize: "0.67rem",
  fontWeight: "700",
  cursor: "pointer",
  transition: "background 0.13s",
};
const FamilyAffectedTrendView = ({
  title,
  data,
  startDateStr,
  endDateStr,
  chartData,
  drillDownKey,
  activeTab,
}) => {
  const { theme } = useContext(ThemeContext);
  const echartsDom = useRef(null);
  const [chart, setChart] = useState(null);
  const [distName, setDistName] = useState();
  const [blockdata, setBlockdata] = useState([]);
  const [blockPopUp, setBlockPopUp] = useState(false);
  const [legendState, setLegendState] = useState({});
  const [labelColor, setLabelColor] = useState("#fff");
  const { t } = useTranslation();
  const [toggleChart, setToggleChart] = useState("bar");
  useEffect(() => {
    if (theme === "dark") setLabelColor("#fff");
    else if (theme === "light") setLabelColor("#909097");
  }, [theme]);
  useEffect(() => {
    if (activeTab === "currentStatus") {
      setToggleChart("trendChart");
    } else {
      setToggleChart("bar");
    }
  }, [activeTab]);

  const handleToggle = () => {
    setBlockPopUp(!blockPopUp);
  };

  const fetchBlockData = async (
    dist_id,
    startDateStr,
    endDateStr,
    drillDownKey
  ) => {
    setBlockPopUp(true);
    try {
      if (activeTab === "dss") {
        const response = await getData(
          getBlockInfoFloodURL
            .replace("TODATE", endDateStr)
            .replace("DISTID", dist_id)
            .replace("APIKEY", drillDownKey)
            .replace("FROMDATE", startDateStr)
        );
        const filteredResponse = response.filter((item) => item.count > 0);
        setBlockdata(filteredResponse);
      } else {
        const response = await getData(
          getBlockInfoFloodURL
            .replace("TODATE", endDateStr)
            .replace("DISTID", dist_id)
            .replace("APIKEY", drillDownKey)
            .replace("FROMDATE", startDateStr)
        );
        const filteredResponse = response.filter((item) => item.count > 0);
        setBlockdata(filteredResponse);
      }
    } catch (error) {
      console.error("Error fetching block data:", error);
    }
  };
  const getChartOptions = () => {
    const sortedData = [...data].sort((a, b) => a.count - b.count);

    return {
      title: {
        left: "center",
        textStyle: {
          color: "#333",
          fontSize: 12,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        confine: true,
        formatter: function (params) {
          const item = sortedData[params[0].dataIndex];
          const name = item?.dist_name || item?.name || "";
          const count = params[0].value || 0;
          return `<div style="padding:4px 8px"><b>${name}</b><br/>Count: <b>${count.toLocaleString("en-IN")}</b></div>`;
        },
      },
      grid: {
        left: "2%",
        top: "8%",
        right: "3%",
        bottom: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        name: title,
        nameLocation: "middle",
        nameTextStyle: {
          padding: [20, 0, 0, 0],
          color: "#909097",
          fontWeight: "bold",
        },
        axisLabel: {
          textStyle: {
            color: "#909097",
            fontWeight: "bold",
          },
        },
      },
      yAxis: {
        type: "category",
        data: sortedData?.map((item) =>
          item?.dist_name ? item?.dist_name : item?.name
        ),
        name: t("flooddisaster.barchartyaxis"),
        nameLocation: "end",
        axisLabel: {
          overflow: "truncate",
          ellipsis: "...",
          show: true,
          color: labelColor,
          fontWeight: "bold",
        },
        axisTick: {
          show: false,
        },
      },
      dataZoom: [
        {
          type: "slider",
          yAxisIndex: 0,
          start: 0,
          end: 100,
          orient: "vertical",
          zoomLock: false,
          handleSize: "100%",
          handleStyle: {
            color: "#0F5698",
          },
        },
      ],
      series: [
        {
          type: "bar",
          barWidth: 3,
          data: sortedData?.map((item) => item?.count),
          itemStyle: {
            color: "#A0C8E0",
            borderRadius: [0, 4, 4, 0],
          },
          showBackground: true,
          backgroundStyle: {
            color: "rgba(42,101,145,.04)",
            borderRadius: [0, 4, 4, 0],
          },
          label: { show: false },
        },
        {
          type: "scatter",
          symbolSize: 22,
          data: sortedData?.map((item) => item?.count),
          itemStyle: {
            color: "#fff",
            borderColor: "#2A6591",
            borderWidth: 3,
          },
          label: {
            show: true,
            position: "right",
            distance: 12,
            formatter: function (data) {
              if (data.value > 0) {
                return `{valueStyle|${data.value.toLocaleString("en-IN")}}`;
              } else {
                return "";
              }
            },
            rich: {
              valueStyle: {
                color: "#2A6591",
                fontSize: 13,
                borderWidth: 1.5,
                borderColor: "rgba(42, 101, 145, 0.2)",
                backgroundColor: "#fff",
                padding: [4, 8, 4, 8],
                fontWeight: "bold",
                borderRadius: 8,
              },
            },
          },
        }
      ],
    };
  };

  const getBaseColors = () => [
    "#2A6591", // Primary Navy
    "#A0C8E0", // Light Sky Blue
    "#1C4E72", // Darkest Navy
    "#5AA0C0", // Medium-Light Blue
    "#3A85B5", // Medium-Dark Blue
    "#7A9BB0", // Muted Muted Blue
    "#0F2D42", // Deep Blue-Black
    "#215175", // Azure
  ];

  // Prepare dateArr and idArr with formatted dates
  let dateArr = [];
  let idArr = [];

  for (var dataElem in chartData) {
    const formattedDate = moment(chartData[dataElem].reported_on_date).format(
      "DD-MM-YY"
    );

    if (!dateArr.includes(formattedDate)) {
      dateArr.push(formattedDate);
    }

    if (!idArr.includes(chartData[dataElem].id)) {
      idArr.push(chartData[dataElem].id);
    }
  }

  dateArr.sort(function (a, b) {
    let dateA = moment(a, "DD-MM-YY").toDate();
    let dateB = moment(b, "DD-MM-YY").toDate();
    return dateA - dateB;
  });

  let seriesArr = [];
  let idSeriesMap = {};

  for (let dataElem in chartData) {
    const formattedDate = moment(chartData[dataElem].reported_on_date).format(
      "DD-MM-YY"
    );

    if (idSeriesMap[chartData[dataElem].id]) {
      let seriesObj = idSeriesMap[chartData[dataElem].id];
      for (let dateElem in dateArr) {
        if (dateArr[dateElem] === formattedDate) {
          seriesObj["datamap"][formattedDate] = chartData[dataElem].count;
        }
      }
    } else {
      let seriesObj = {};

      seriesObj["name"] = chartData[dataElem].name;
      seriesObj["type"] = "line";

      let dateCountDataMap = {};

      for (let dateElem in dateArr) {
        if (dateArr[dateElem] === formattedDate) {
          dateCountDataMap[dateArr[dateElem]] = chartData[dataElem].count;
        } else {
          dateCountDataMap[dateArr[dateElem]] = null;
        }
      }

      seriesObj["datamap"] = dateCountDataMap;
      idSeriesMap[chartData[dataElem].id] = seriesObj;
    }
  }

  for (let series in idSeriesMap) {
    idSeriesMap[series]["data"] = Object.values(idSeriesMap[series]["datamap"]);
  }
  const getTrendChartOptions = () => {
    const seriesNames = Object.values(idSeriesMap).map(
      (seriesObj) => seriesObj.name
    );
    const lineColors = getBaseColors();

    return {
      tooltip: {
        show: true,
        trigger: "axis",
        confine: true,
        formatter: (params) => {
          const date = params[0]?.name;
          let tooltipContent = `<b>${date}</b><br/>`;

          params.forEach((p) => {
            if (p.value === null || p.value === undefined || p.value === 0) return;
            const seriesName = p.seriesName;
            const districtData = chartData.find(
              (item) =>
                item.name === seriesName &&
                moment(item.reported_on_date).format("DD-MM-YY") === date
            );
            tooltipContent += `${p.marker} ${seriesName}: <b>${p.value}</b><br/>`;
            if (districtData?.blk_name) {
              const blkNamesArray = districtData.blk_name.split(",");
              const limitedBlkNames = blkNamesArray.reduce((acc, curr, index) => {
                const groupIndex = Math.floor(index / 2);
                acc[groupIndex] = acc[groupIndex] || [];
                acc[groupIndex].push(curr);
                return acc;
              }, []);
              tooltipContent += `Blocks:<br/>`;
              limitedBlkNames.forEach((group) => {
                tooltipContent += `${group.join(", ")}<br/>`;
              });
            }
          });

          return tooltipContent;
        },
      },

      legend: {
        show: false,
        selected: Object.values(idSeriesMap).reduce((acc, seriesObj) => {
          acc[seriesObj.name] = legendState[seriesObj.name] !== false;
          return acc;
        }, {}),
      },
      xAxis: {
        type: "category",
        data: dateArr,
        axisLabel: {
          rotate: 45,
          textStyle: { color: labelColor },
        },
      },
      dataZoom: [
        {
          startValue: "2014-06-01",
        },
        {
          type: "inside",
        },
      ],
      grid: {
        left: "5%",
        top: "10%",
        right: "3%",
        bottom: "20%",
        containLabel: true,
      },
      yAxis: {
        type: "value",
        axisLabel: {
          textStyle: { color: labelColor },
        },
      },
      series: Object.values(idSeriesMap).map((seriesObj, index) => ({
        ...seriesObj,
        data: seriesObj.data.map((value) => (value === null ? 0 : value)),
        smooth: true,
        symbolSize: 6,
        showSymbol: false,
        emphasis: {
          focus: "series",
        },
        lineStyle: {
          width: 3,
          color: lineColors[index % lineColors.length],
        },
        itemStyle: {
          color: lineColors[index % lineColors.length],
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: lineColors[index % lineColors.length] },
            { offset: 1, color: "rgba(255,255,255,0.01)" }
          ]),
          opacity: 0.3
        }
      })),
    };
  };

  useEffect(() => {
    if ((data && data.length > 0) || (chartData && chartData.length > 0)) {
      if (echartsDom.current) {
        const myChart = echarts.init(echartsDom.current);
        setChart(myChart);

        const handleResize = () => {
          myChart.resize();
        };
        window.addEventListener("resize", handleResize);

        const resizeObserver = new ResizeObserver(() => {
          myChart.resize();
        });
        resizeObserver.observe(echartsDom.current);

        return () => {
          window.removeEventListener("resize", handleResize);
          resizeObserver.disconnect();
          myChart.dispose();
        };
      }
    }
  }, [data, toggleChart, chartData, legendState]);

  useEffect(() => {
    if (chart) {
      chart.clear();

      chart.off("click");
      if (toggleChart === "bar" && chartData && chartData.length > 0) {
        chart.setOption(getTrendChartOptions());
      } else if (
        toggleChart === "trendChart" &&
        data &&
        data.length > 0
      ) {
        const sortedForClick = [...data].sort((a, b) => a.count - b.count);
        chart.setOption(getChartOptions());
        chart.on("click", function (params) {
          if (params.dataIndex !== undefined) {
            const item = sortedForClick[params.dataIndex];
            if (item) {
              setDistName(item.dist_name || item.name);
              fetchBlockData(item.dist_id, startDateStr, endDateStr, drillDownKey);
            }
          }
        });
      } else {
        chart.clear();
      }
    }
  }, [chart, toggleChart, data, chartData, t, theme, labelColor]);

  const componentRender = () => {
    if (toggleChart === "bar") {
      if (chartData?.length > 0) {
        return (
          <div ref={echartsDom} style={{ flex: 1, width: "100%", minHeight: 0 }} />
        );
      } else {
        return <NoData />;
      }
    } else {
      if (data?.length > 0) {
        return (
          <div ref={echartsDom} style={{ flex: 1, width: "100%", minHeight: 0 }} />
        );
      } else {
        return <NoData />;
      }
    }
  };
  return (
    <>
      <div className="card shadow-sm border-0" style={{ width: "100%", height: "100%", borderRadius: "14px", background: "var(--av-surface)", minHeight: "440px", padding: "8px" }}>
        <div className="card-body d-flex flex-column" style={{ height: "100%" }}>
          <div className="d-flex align-items-center gap-4" style={{ marginBottom: "16px" }}>
            <div className="card-title mb-0" style={{ color: "#2A6591", fontWeight: "700", whiteSpace: "nowrap", fontSize: "1.1rem" }}>
              {title}
            </div>
            {activeTab !== "currentStatus" && (
              <div className="d-flex align-items-center gap-3">
                <ToggleView
                  setActiveTab={setToggleChart}
                  activeTab={toggleChart}
                />
                {toggleChart === "bar" && (() => {
                  const allSelected = Object.values(idSeriesMap).every(s => legendState[s.name] !== false);
                  return (
                    <div className="d-flex align-items-center gap-3">
                      {/* <button
                        onClick={() => {
                          if (allSelected) {
                            const newState = {};
                            Object.values(idSeriesMap).forEach(s => newState[s.name] = false);
                            setLegendState(newState);
                          } else {
                            setLegendState({});
                          }
                        }}
                        style={buttonStyle}
                      >
                        {allSelected ? "Deselect All" : "Select All"}
                      </button> */}
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        {Object.values(idSeriesMap).map((seriesObj, index) => {
                          const colors = getBaseColors();
                          const color = colors[index % colors.length];
                          const isVisible = legendState[seriesObj.name] !== false;
                          return (
                            <div key={index} className="d-flex align-items-center gap-2" 
                              onClick={() => setLegendState(prev => ({ ...prev, [seriesObj.name]: !isVisible }))}
                              style={{ opacity: isVisible ? 1 : 0.4, cursor: "pointer" }}>
                              <span style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                background: color,
                                display: "inline-block",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                              }} />
                              <span style={{ fontSize: "0.72rem", fontWeight: "700", color: "#3A5F78", userSelect: "none" }}>
                                {seriesObj.name}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {componentRender()}
        </div>
      </div>
      {blockPopUp && (
        <BlockPopUp
          title={`Affected District : ${distName}`}
          distName={distName}
          show={blockPopUp}
          handleToggle={handleToggle}
          blockdata={blockdata}
          component={<BlockDetails blockdata={blockdata} title={title} />}
        />
      )}
    </>
  );
};

export default FamilyAffectedTrendView;
